'use client';

import { createContext, useContext, useMemo, useRef } from 'react';

/**
 * Scene-level "already introduced" registry for the Live Tap renderer.
 *
 * TT-961: once a piece of text (or a symbol) has drawn into the scene with its
 * chalk/pen intro, it must never replay that intro again — no matter where it
 * later moves (repacked to a new spot, swapped into a curated slot, or handed
 * from the generative renderer to the curated one). Elements consult this
 * registry at reveal time via `reveal(key)`:
 *
 *   - true  → this key is being introduced at the current playhead; the element
 *             plays its pen-trace / chalk reveal.
 *   - false → this key was introduced at an earlier sentence; the element
 *             renders straight to its final state with no pen and no dust.
 *
 * Playhead-derived (per TT-961): "seen" is tied to the playhead, not a
 * permanent session fact. Scrubbing the playhead BACKWARD past a key's intro
 * forgets it, so a later forward pass re-earns the intro — matching the
 * reproducible-replay model the rest of the renderer follows (reduceTapestry).
 * Scrubbing back to exactly a key's intro frame replays it.
 *
 * The registry is a ref (not state): consulting it must not trigger renders,
 * and the recorded first-seen frame is bookkeeping, not rendered data.
 */

const SeenRevealContext = createContext(null);

/** Normalize text so the same content matches across renderers/phases. */
export function normalizeSeenKey(text) {
  return (text || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export function SeenRevealProvider({ currentSent, instant = false, children }) {
  const stateRef = useRef({ seen: new Map(), lastSent: null });
  // Current playhead (a sentence ordinal), mirrored into a ref so the stable
  // `reveal` closure reads the live value without the api identity changing.
  const sentRef = useRef(currentSent);
  sentRef.current = currentSent;

  // Prune on scrub-back: drop any key first seen AFTER the new (earlier)
  // playhead so scrubbing forward again replays its intro. Done in render (not
  // an effect) so the map is already pruned before children consult it in the
  // same commit.
  const state = stateRef.current;
  if (state.lastSent !== null && currentSent < state.lastSent) {
    for (const [k, sent] of state.seen) {
      if (sent > currentSent) state.seen.delete(k);
    }
  }
  state.lastSent = currentSent;

  const api = useMemo(
    () => ({
      reveal(rawKey) {
        const key = normalizeSeenKey(rawKey);
        if (!key) return true; // no stable identity → always treat as new
        const seenAt = state.seen.get(key);
        if (seenAt === undefined) {
          state.seen.set(key, sentRef.current);
          return true;
        }
        // Already recorded: only replay at its own intro frame (scrub-to-intro).
        return seenAt === sentRef.current;
      },
    }),
    // state + sentRef are stable refs; the api must stay stable so consumer
    // effects keyed on it don't re-run as the playhead advances.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return <SeenRevealContext.Provider value={instant ? STATIC_API : api}>{children}</SeenRevealContext.Provider>;
}

export function useSeenReveal() {
  return useContext(SeenRevealContext);
}

// `static` tells consumers to skip even the seen-crossfade (no rAF at all).
const STATIC_API = { reveal: () => false, static: true };

/**
 * Registry that reports everything as already seen — every element renders
 * straight to its final state with no pen-trace or chalk. For static
 * previews (scene composer, contact sheets, tests).
 */
export function StaticSeenRevealProvider({ children }) {
  return <SeenRevealContext.Provider value={STATIC_API}>{children}</SeenRevealContext.Provider>;
}
